import React from 'react';
import { TrendingUp, TrendingDown, RefreshCw, AlertCircle } from 'lucide-react';

const Positions = ({ positions, onRefresh }) => {
  if (!positions || positions.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Open Positions</h2>
          <button
            onClick={onRefresh}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
        
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">No Open Positions</h3>
          <p className="text-gray-500 text-center">
            Start trading to see your positions here. All positions will be displayed with real-time P&L updates.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Open Positions</h2>
        <button
          onClick={onRefresh}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 text-gray-300 font-medium">Symbol</th>
              <th className="text-left py-3 px-4 text-gray-300 font-medium">Side</th>
              <th className="text-right py-3 px-4 text-gray-300 font-medium">Size</th>
              <th className="text-right py-3 px-4 text-gray-300 font-medium">Entry Price</th>
              <th className="text-right py-3 px-4 text-gray-300 font-medium">Current Price</th>
              <th className="text-right py-3 px-4 text-gray-300 font-medium">P&L</th>
              <th className="text-center py-3 px-4 text-gray-300 font-medium">Leverage</th>
              <th className="text-center py-3 px-4 text-gray-300 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position) => (
              <tr key={position.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-4 px-4">
                  <div className="flex items-center">
                    <span className="font-medium text-white">
                      {position.symbol.replace('/USDT', '')}
                    </span>
                  </div>
                </td>
                
                <td className="py-4 px-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    position.side === 'long' 
                      ? 'bg-green-900/50 text-green-300 border border-green-700' 
                      : 'bg-red-900/50 text-red-300 border border-red-700'
                  }`}>
                    {position.side === 'long' ? (
                      <TrendingUp className="w-3 h-3 mr-1" />
                    ) : (
                      <TrendingDown className="w-3 h-3 mr-1" />
                    )}
                    {position.side.toUpperCase()}
                  </span>
                </td>
                
                <td className="py-4 px-4 text-right text-white">
                  {position.size.toFixed(4)}
                </td>
                
                <td className="py-4 px-4 text-right text-white">
                  ${position.entry_price.toLocaleString()}
                </td>
                
                <td className="py-4 px-4 text-right text-white">
                  ${position.current_price.toLocaleString()}
                </td>
                
                <td className="py-4 px-4 text-right">
                  <div className="flex flex-col items-end">
                    <span className={`font-medium ${
                      position.pnl >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      ${position.pnl.toFixed(2)}
                    </span>
                    <span className={`text-xs ${
                      position.pnl_percentage >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      ({position.pnl_percentage >= 0 ? '+' : ''}{position.pnl_percentage.toFixed(2)}%)
                    </span>
                  </div>
                </td>
                
                <td className="py-4 px-4 text-center">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300 border border-blue-700">
                    {position.leverage}x
                  </span>
                </td>
                
                <td className="py-4 px-4 text-center">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    position.status === 'open' 
                      ? 'bg-green-900/50 text-green-300 border border-green-700'
                      : 'bg-gray-700 text-gray-300 border border-gray-600'
                  }`}>
                    {position.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-700/50 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Total Positions</p>
          <p className="text-white text-lg font-semibold">{positions.length}</p>
        </div>
        
        <div className="bg-gray-700/50 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Total P&L</p>
          <p className={`text-lg font-semibold ${
            positions.reduce((sum, pos) => sum + pos.pnl, 0) >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            ${positions.reduce((sum, pos) => sum + pos.pnl, 0).toFixed(2)}
          </p>
        </div>
        
        <div className="bg-gray-700/50 rounded-lg p-4">
          <p className="text-gray-400 text-sm">Total Value</p>
          <p className="text-white text-lg font-semibold">
            ${positions.reduce((sum, pos) => sum + (pos.size * pos.current_price), 0).toFixed(2)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Positions;